'use client';
import React, { useMemo } from 'react';
import { Flex, Grid, GridItem, Text, useBreakpointValue } from '@chakra-ui/react';
import { useProductService } from '@/hooks/product/useProductService';
import { useReviewService } from '@/hooks/review/useReviewService';
import { ProductStockStatus } from '@/constants/product/product';
import BestSellersListSkeleton from './BestSellersListSkeleton';
import ProductCard from '@/components/products/ProductCard';
import { useSafeRouter } from '@/hooks/route/useSafeRouter';
import { createReviewStatsMap } from '@/utils/domain/review';
import { getLowestPriceProductBasicInfo } from '@/utils/domain/product';

const BestSellersList = () => {
  const { useGetProductsCollections } = useProductService();
  const { useGetReviewStatistics } = useReviewService();
  const { push } = useSafeRouter();

  const { data } = useGetProductsCollections({
    collectionName: 'Best Seller',
    limit: 5,
    stockStatus: ProductStockStatus.instock,
  });

  const productLimit = useBreakpointValue({ base: 4, desktop: 5 });

  const products = useMemo(() => {
    return data?.products?.length ? data.products : [];
  }, [data]);

  const productIds = useMemo(() => {
    if (products.length === 0) {
      return '';
    }
    return products.map((product) => product.productId).join(',');
  }, [products]);

  const { data: reviewStats } = useGetReviewStatistics({ productIds });

  const reviewStatsMap = useMemo(() => createReviewStatsMap(reviewStats), [reviewStats]);

  if (products.length === 0) {
    return (
      <Flex width="100%" justifyContent="center" alignItems="center" padding="60px 0">
        <Text color="neutral.60" textStyle="body2" fontWeight="medium" textAlign="center">
          Currently the Best Seller Collection is Empty
        </Text>
      </Flex>
    );
  }

  return (
    <Grid
      templateColumns={{
        desktop: 'repeat(5, 1fr)',
        base: 'repeat(2, 1fr)',
      }}
      tablet={{
        '& > *:nth-of-type(n+5)': {
          display: 'none',
        },
      }}
      desktop={{
        '& > *:nth-of-type(n+5)': {
          display: 'initial',
        },
      }}
      gap={3}
      width="100%"
    >
      {products.slice(0, productLimit).map((product) => {
        const productInfo = getLowestPriceProductBasicInfo(product);
        const productReviewStats = reviewStatsMap[product.productId];
        return (
          <GridItem key={product.productId}>
            <ProductCard
              showRating
              key={product.productId}
              onClick={() => push(productInfo.productLink)}
              name={productInfo.name}
              brand={productInfo.brand}
              productImage={productInfo.productImageUrls[0]}
              sku={productInfo.sku}
              currency={productInfo.currency}
              originalPrice={productInfo.originalPrice}
              salePrice={productInfo.salePrice}
              rating={productReviewStats?.averageRate}
              reviewCount={productReviewStats?.totalReviews}
            />
          </GridItem>
        );
      })}
    </Grid>
  );
};

const BestSellersListWithSkeleton = BestSellersList as typeof BestSellersList & {
  Skeleton: typeof BestSellersListSkeleton;
};

BestSellersListWithSkeleton.Skeleton = BestSellersListSkeleton;

export default BestSellersListWithSkeleton;
